/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useHistory, useLocation, useParams, useRouteMatch} from "react-router-dom";
import {useTypedSelector} from "helpers/reducers";
import {Package} from "types/entities/Package";
import {useDispatch} from "useDispatch";
import {PackagesCart} from "types/entities/PackagesCart";
import {HallCode} from "types/enums/HallCode";
import VoucherAction from "store/shop/VoucherAction";
import {PaymentStatus} from "types/enums/PaymentStatus";
import {ThunkAction} from "redux-thunk";
import BookingAction from "store/booking/BookingAction";
import {packageHasUpsellItems} from "utils/packageUtils";
import {useEffect} from "react";
import {getNewStep} from "utils/utility";
import {
    AllowedStep,
    TIMESLOTS_ALLOWED_STEPS,
    TIMESLOTS_ALLOWED_STEPS_FOR_GIFT,
    TIMESLOTS_ALLOWED_STEPS_FOR_SUBSCRIPTION,
    TIMESLOTS_UPSELLS_ALLOWED_STEPS,
} from "store/booking/BookingReducer";
import {IS_UPSELL_ALLOWED} from "helpers/config";
import {PackageCategory} from "types/enums/PackageCategory";

export enum RouteName {
    Main = "booking",
    TimeslotsNew = "timeslots",
    Options = "options",
    Packages = "packages",
    BookingReservations = "login/reservations",
    SellerLogin = "seller/login",
    BookingLogin = "login",
    Gift = "package/5",
    Subscription = "package/6",
    ThreeTwo = "package/21",
    PackageDetail = "package",
    Upsells = "upsell",
    UpsellsNew = "upsells",
    Timeslots = "book",
    Customer = "customer",
    Inquiry = "inquiry",
    Checkin = "status-booking",
    Paid = "paid",
    GiftCheckout = "checkout",
    Competitions = "competitions",
}

const useNavigation = () => {
    const category = useTypedSelector(state => state.bookingState.bookingProcess?.filter?.category);
    const bookingNavigation = useTypedSelector(state => state.bookingState.navigation);
    const bookingState = useTypedSelector(state => state.bookingState);
    const hall = useTypedSelector(state => state.hallState.hall);
    const payment = useTypedSelector(state => state.bookingState.bookingProcess.payment);
    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const dispatch = useDispatch();
    const location = useLocation();
    const params = useParams();
    const history = useHistory();
    const match = useRouteMatch();
    const prefix = "/booking/";
    const queryParams = new URLSearchParams(location.search as any);

    const getIsCompetitionItemsAvailable = () => {
        const packageItems = bookingState.bookingProcess?.availablePackageItems;
        return Boolean(packageItems?.competitionItems?.length || packageItems?.includedCompetitionItems?.length);
    };

    const isHomeBookingRoute = () => {
        return location.pathname === "/booking" || location.pathname === "/booking/";
    };

    const isBookingRoute = (): boolean => {
        return location.pathname.includes("booking") && !location.pathname.includes("booking/login");
    };

    const isBookingSellerRoute = (): boolean => {
        return location.pathname.includes("/booking/seller");
    };

    const isGiftBookingRoute = () => {
        return location.pathname === "/booking/package/5" || location.pathname === "/booking/package/5/";
    };

    const isSubscriptionBookingRoute = () => {
        return location.pathname === "/booking/package/6" || location.pathname === "/booking/package/6/";
    };

    const isThreeTwoBookingRoute = () => {
        return location.pathname === "/booking/package/21" || location.pathname === "/booking/package/21/";
    };

    const isGiftCheckoutBookingRoute = () => {
        return location.pathname === "/booking/checkout" || location.pathname === "/booking/checkout/";
    };

    const isSubscriptionPages = () => {
        return isSubscriptionBookingRoute() || (isGiftCheckoutBookingRoute() && category === PackageCategory.Yearly);
    };

    const isThreeTwoPages = () => {
        return isThreeTwoBookingRoute() || (isGiftCheckoutBookingRoute() && category === PackageCategory.ThreeTwo);
    };

    const isGiftPages = () => {
        return isGiftBookingRoute() || (isGiftCheckoutBookingRoute() && category === PackageCategory.Giftcard);
    };

    const getStepsArray = () => {
        if (isGiftPages()) return TIMESLOTS_ALLOWED_STEPS_FOR_GIFT;
        if (isThreeTwoPages()) return TIMESLOTS_ALLOWED_STEPS_FOR_SUBSCRIPTION;
        if (IS_UPSELL_ALLOWED) return TIMESLOTS_UPSELLS_ALLOWED_STEPS;
        return TIMESLOTS_ALLOWED_STEPS;
    };
    const stepsArray = getStepsArray();

    const bookingRoutes = (_package: Package, routeName: RouteName, packagesCart?: PackagesCart) => {
        switch (routeName) {
            case RouteName.Main:
                return prefix;
            case RouteName.Options:
                return prefix + routeName + "/" + _package.rewrite_url;
            case RouteName.TimeslotsNew:
                return prefix + routeName + "/" + _package.rewrite_url;
            case RouteName.Packages:
                return prefix + routeName + "/" + _package.rewrite_url;
            case RouteName.GiftCheckout:
                return prefix + routeName + "/" + _package.rewrite_url;
            case RouteName.Gift:
                return prefix + routeName + "/" + _package.rewrite_url;
            case RouteName.Subscription:
                return prefix + routeName + "/" + _package.rewrite_url;
            case RouteName.ThreeTwo:
                return prefix + routeName + "/" + _package.rewrite_url;
            case RouteName.PackageDetail:
                return prefix + routeName + "/" + _package.package_id + "/" + _package.rewrite_url;
            case RouteName.Upsells:
                return prefix + routeName + "/" + _package.package_id + "/" + _package.rewrite_url;
            case RouteName.Competitions:
                return prefix + routeName + "/" + _package.package_id + "/" + _package.rewrite_url;
            case RouteName.UpsellsNew:
                return prefix + routeName + "/" + _package.package_id + "/" + _package.rewrite_url;
            case RouteName.Timeslots:
                return prefix + routeName + "/" + _package.package_id + "/" + _package.rewrite_url;
            case RouteName.Customer:
                return prefix + routeName;
            case RouteName.Checkin:
                // <Route path="/status-booking/:package_cart_guid?/:package_cart_token?/:app_id?/:m_clk_id?">
                return "/" + routeName + "/" + packagesCart?.guid + "/" + packagesCart?.token;
            case RouteName.Inquiry:
                return prefix + routeName;
            case RouteName.BookingLogin:
                return prefix + routeName + "/";
            case RouteName.SellerLogin:
                return prefix + routeName + "/";
            case RouteName.BookingReservations:
                return prefix + routeName + "/";
            case RouteName.Paid:
                return prefix + routeName;
            default:
                return prefix;
        }
    };

    const getCurrentPage = (): RouteName => {
        const path = location.pathname;
        const routes = [
            RouteName.Gift,
            RouteName.Subscription,
            RouteName.ThreeTwo,
            RouteName.GiftCheckout,
            RouteName.Options,
            RouteName.TimeslotsNew,
            RouteName.Packages,
            RouteName.Customer,
            RouteName.UpsellsNew,
            RouteName.Competitions,
            RouteName.Paid,
            RouteName.Main,
        ];

        const route = routes.find(route => path.includes(route));
        if (!route) {
            return RouteName.Main;
        }

        return route;
    };

    const getCurrentStep = (routeName: RouteName): AllowedStep => {
        const step = stepsArray.find(step => step.routeName === routeName);
        if (step === undefined) {
            return TIMESLOTS_ALLOWED_STEPS[0];
        }

        return step;
    };

    const packageRoutes = (routeName: RouteName) => {
        if (!selectedPackage) {
            return prefix;
        }
        return bookingRoutes(selectedPackage, routeName);
    };

    const historyPush = (
        routeName: RouteName,
        origin = "default",
        selectedPackage?: Package | undefined | null,
        packagesCart?: PackagesCart | null | undefined
    ) => {
        if (routeName === RouteName.BookingReservations || routeName === RouteName.BookingLogin) {
            return history.push("/booking/" + routeName);
        }

        if (routeName === RouteName.SellerLogin) {
            return history.push("/booking/" + routeName);
        }

        if (
            routeName === RouteName.Options ||
            routeName === RouteName.TimeslotsNew ||
            routeName === RouteName.Packages ||
            routeName === RouteName.Competitions ||
            routeName === RouteName.UpsellsNew ||
            routeName === RouteName.Gift ||
            routeName === RouteName.Subscription ||
            routeName === RouteName.ThreeTwo ||
            routeName === RouteName.GiftCheckout
        ) {
            return history.push("/booking/" + routeName + "?" + queryParams.toString());
        }

        if (packagesCart && selectedPackage) {
            return history.push(bookingRoutes(selectedPackage, routeName, packagesCart) + "?" + queryParams.toString());
        }

        if (selectedPackage) {
            return history.push(bookingRoutes(selectedPackage, routeName) + "?" + queryParams.toString());
        }

        return history.push(packageRoutes(routeName) + "?" + queryParams.toString());
    };

    const restartProcess = (force: boolean = false) => {
        if (force) {
            return history.replace(prefix);
        }
        return history.push(prefix);
    };

    const titleText = (page_url: string) => {
        if (hall) {
            return page_url + (hall.hall_code == HallCode.ArboPark ? " | ArboPark – Action & Fun Center" : "");
        }
        return page_url;
    };

    const handleRouteNavigation = () => {
        dispatch(VoucherAction.resetAppliedVouchers());
        if (payment?.status === PaymentStatus.Started) {
            // dispatch<ThunkAction<any, any, any, any>>(
            //   BookingAction.initCart()).then(r => props.history.push("/booking"));
            // return;
            return dispatch<ThunkAction<any, any, any, any>>(BookingAction.initCart()).then(r => {
                dispatch(BookingAction.clearBookingProcess());
                return historyPush(RouteName.Main, "paymentStarted");
            });
        }

        if (payment?.status === PaymentStatus.Finished || payment?.status === PaymentStatus.Authorized || payment?.status === PaymentStatus.Captured) {
            //  dispatch<ThunkAction<any, any, any, any>>(
            //                 BookingAction.initCart()).then(r => props.history.push("/booking"));
            //             return;
            return dispatch<ThunkAction<any, any, any, any>>(BookingAction.initCart()).then(r => {
                dispatch(BookingAction.clearBookingProcess());
                return historyPush(RouteName.Main, "paymentFinished");
            });
        }

        if (location.pathname.includes("/booking/inquiry")) {
            // const packageLocation = location.replace('upsell', 'package');
            // props.history.replace(packageLocation);
            // return;
            return dispatch<ThunkAction<any, any, any, any>>(BookingAction.initCart()).then(r => {
                dispatch(BookingAction.clearBookingProcess());
                return historyPush(RouteName.Main, "bookingInquiry");
            });
        }

        if (location.pathname.includes("/booking/competitions")) {
            return historyPush(RouteName.Packages, "competitions");
        }

        if (location.pathname.includes("/booking/upsells")) {
            const isCompetitionItemsAvailable = getIsCompetitionItemsAvailable();
            if (isCompetitionItemsAvailable) {
                return historyPush(RouteName.Competitions, "upsells");
            }
            return historyPush(RouteName.Packages, "upsells");
        }

        if (location.pathname.includes("/booking/upsell")) {
            return historyPush(RouteName.Packages, "upsells");
        }

        if (location.pathname.includes("/booking/packages")) {
            return historyPush(RouteName.TimeslotsNew, "packages");
        }

        if (location.pathname.includes("/booking/timeslots")) {
            return historyPush(RouteName.Options, "timeslots");
        }

        if (location.pathname.includes("/booking/options")) {
            return historyPush(RouteName.Main, "options");
        }

        if (location.pathname.includes("/booking/package/5")) {
            return historyPush(RouteName.Main, "gift");
        }

        if (location.pathname.includes("/booking/package/6")) {
            return historyPush(RouteName.Main, "subscription");
        }
        if (location.pathname.includes("/booking/package/21")) {
            return historyPush(RouteName.Main, "subscription");
        }

        if (location.pathname.includes("/booking/checkout") && category === PackageCategory.Giftcard) {
            return historyPush(RouteName.Gift, "giftCheckout");
        }

        if (location.pathname.includes("/booking/checkout") && category === PackageCategory.Yearly) {
            return historyPush(RouteName.Subscription, "giftCheckout");
        }

        if (location.pathname.includes("/booking/checkout") && category === PackageCategory.ThreeTwo) {
            return historyPush(RouteName.ThreeTwo, "giftCheckout");
        }

        if (location.pathname.includes("/booking/book")) {
            let has_upsells = false;
            if (selectedPackage) {
                has_upsells = packageHasUpsellItems(selectedPackage);
            }
            if (has_upsells) {
                return historyPush(RouteName.Upsells, "timeslots", selectedPackage);
            } else {
                if (selectedPackage) {
                    dispatch(BookingAction.clearBookingProcess(null));
                    dispatch(BookingAction.initCart(true)).then(r => {
                        return;
                    });
                }
                return historyPush(RouteName.Main, "timeslots", selectedPackage);
            }

            // dispatch(BookingAction.setPackage(null));
            // history.goBack();
            // return;
        }

        if (location.pathname.includes("/booking/package")) {
            // props.history.replace("/booking");
            // return;
            dispatch(BookingAction.setDateChosenByCustomer(false));

            if (selectedPackage) {
                console.log("going to clear booking process");
                dispatch(BookingAction.clearBookingProcess(null));
                dispatch(BookingAction.initCart(true)).then(r => {
                    return;
                });
            }
            return historyPush(RouteName.Main, "package", selectedPackage);
        }

        if (location.pathname.includes("/booking/terms-and-conditions")) {
            // props.history.replace("/booking");
            // return;
            dispatch(BookingAction.setDateChosenByCustomer(false));
            return historyPush(RouteName.Main, "terms", null);
        }

        if (location.pathname.includes("/booking/impressum")) {
            // props.history.replace("/booking");
            // return;
            dispatch(BookingAction.setDateChosenByCustomer(false));
            return historyPush(RouteName.Main, "impressum", null);
        }

        if (location.pathname.includes("/booking/login/reservations/detail/")) {
            dispatch(BookingAction.setDateChosenByCustomer(false));
            return historyPush(RouteName.BookingReservations, "reservations-detail", null);
        }

        if (location.pathname.includes("/booking/login/reservations-redesign/detail/")) {
            dispatch(BookingAction.setDateChosenByCustomer(false));
            return historyPush(RouteName.BookingReservations, "reservations-detail-redesign", null);
        }

        if (location.pathname.includes("/booking/login/reservations")) {
            // props.history.replace("/booking");

            // return;
            dispatch(BookingAction.setDateChosenByCustomer(false));
            return historyPush(RouteName.Main, "reservations", null);
        }

        history.goBack();
    };

    useEffect(() => {
        if (!bookingNavigation?.currentStep) {
            dispatch(BookingAction.setCurrentNavigationStep(getNewStep(bookingState, 1)));
        }
    }, [bookingNavigation]);

    return {
        location,
        history,
        match,
        params,
        bookingRoutes,
        getCurrentPage,
        getCurrentStep,
        handleRouteNavigation,
        historyPush,
        isHomeBookingRoute,
        packageRoutes,
        restartProcess,
        stepsArray,
        titleText,
        isBookingRoute,
        isGiftBookingRoute,
        isGiftCheckoutBookingRoute,
        isBookingSellerRoute,
        getIsCompetitionItemsAvailable,
    };
};

export default useNavigation;
